import { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useStyles } from './MemberDisconnected.styles';
import InternetDisconnected from './../../../../../../../assets/images/internet-disconnected.png';
import { btnType, Button } from '../../../../../atoms/button';
import { MEMBER_ACTIONS } from '../../../sessions.constants';
import { Input, inputSize } from '../../../../../atoms/input';
import { sendMessage } from '../../../../../../../services/report-service/reportService.service';
import { showSnackbar } from '../../../../../../../redux/modules/snackbar';
import { getAuth } from '../../../../../../../redux/modules/auth/selectors';
import { TWILIO_CALL_STATE } from '../../../../twilio-call/twilio.constants';
import { Icons } from '../../../../../../icons';
import { iconBtnType, IconButton } from '../../../../../molecules/icon-button';
import { colors } from '../../../../../../colors';
import ringingImage from '../../../../../../../assets/images/ringing-phone.png';
import connectedImage from '../../../../../../../assets/images/phone-connected.png';
import {
  isValidUSPhoneNumber,
  formatPhoneNumber,
  normalizePhoneNumber,
} from '../../../../../../../utils/phoneUtils';
import config from '../../../../../../../config';

const MemberDisconnected = ({
  mode = 'disconnected',
  appointment,
  firstName,
  phoneNumber,
  duration,
  serviceDuration,
  onMemberActionClick,
  mediaPermissionsGranted,
  memberMediaPermissionsGranted = true,
  twilioCallHandlers,
  twilioCallState = TWILIO_CALL_STATE.IDLE,
  onGracePeriodExpired,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { meta } = useSelector(getAuth);
  const remainingDuration = serviceDuration - duration / 60;
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isGracePeriod, setIsGracePeriod] = useState(false);
  const [textMessage, setTextMessage] = useState('');
  const [isSendingText, setIsSendingText] = useState(false);
  const [graceTimerSeconds, setGraceTimerSeconds] = useState(5 * 60); // 5 minutes in seconds

  // Twilio call UI states
  const [isAudio, setIsAudio] = useState(true);
  const [callConnectedTimer, setCallConnectedTimer] = useState(0);
  const connectedCallIntervalRef = useRef(null);

  useEffect(() => {
    if (mode !== 'waiting' || !appointment) return;

    const calculateTime = () => {
      const now = dayjs();
      const startTime = dayjs(appointment.startTime);
      const diff = startTime.diff(now);

      if (diff > 0) {
        const totalSeconds = Math.floor(diff / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (days > 0) {
          setTimeRemaining(`${days} day${days > 1 ? 's' : ''}`);
        } else if (hours > 0) {
          setTimeRemaining(
            `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
          );
        } else {
          setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
        setIsGracePeriod(false);
      } else {
        setIsGracePeriod(true);
      }
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [mode, appointment]);

  // Grace period countdown timer (restarts on refresh)
  useEffect(() => {
    if (!isGracePeriod) return;

    if (graceTimerSeconds <= 0) {
      setTimeRemaining('expired');
      return;
    }

    const timer = setInterval(() => {
      setGraceTimerSeconds(prev => {
        if (prev <= 1) {
          setTimeRemaining('expired');
          return 0;
        }
        const newSeconds = prev - 1;
        const minutes = Math.floor(newSeconds / 60);
        const seconds = newSeconds % 60;
        setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        return newSeconds;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isGracePeriod]);

  // Notify parent when grace period expires
  useEffect(() => {
    if (mode === 'waiting' && timeRemaining === 'expired' && onGracePeriodExpired) {
      onGracePeriodExpired();
    }
  }, [mode, timeRemaining, onGracePeriodExpired]);

  // Call timer for Twilio
  useEffect(() => {
    if (twilioCallState === TWILIO_CALL_STATE.CONNECTED && !connectedCallIntervalRef.current) {
      connectedCallIntervalRef.current = setInterval(() => {
        setCallConnectedTimer(prev => prev + 1);
      }, 1000);
    }
    if (twilioCallState === TWILIO_CALL_STATE.DISCONNECTED || twilioCallState === TWILIO_CALL_STATE.IDLE) {
      if (connectedCallIntervalRef.current) {
        clearInterval(connectedCallIntervalRef.current);
        connectedCallIntervalRef.current = null;
      }
      setCallConnectedTimer(0);
    }

    return () => {
      if (connectedCallIntervalRef.current) {
        clearInterval(connectedCallIntervalRef.current);
      }
    };
  }, [twilioCallState]);

  const handleSendText = async () => {
    if (textMessage.trim() && !isSendingText) {
      setIsSendingText(true);

      try {
        const messageData = {
          memberId: appointment?.patientId,
          userId: meta?.userId,
          content: textMessage.trim(),
        };

        await sendMessage(messageData);

        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Text message sent successfully',
          }),
        );

        setTextMessage('');
      } catch (error) {
        console.error('Error sending text message:', error);

        if (error.response?.data?.message) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: `Failed to send message: ${error.response.data.message}`,
            }),
          );
        } else {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Failed to send message. Please try again.',
            }),
          );
        }
      } finally {
        setIsSendingText(false);
      }
    }
  };

  const toggleMic = () => setIsAudio(!isAudio);

  /**
   * Handle phone call with validation
   */
  const handlePhoneCall = () => {
    const { callHandler } = twilioCallHandlers;
    const displayPhone = mode === 'waiting' ? appointment?.patientPhoneNumber || phoneNumber : phoneNumber;

    // Validate phone number
    if (!isValidUSPhoneNumber(displayPhone)) {
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Invalid phone number. Please provide a valid US phone number.',
        }),
      );
      return;
    }

    // Normalize phone number for API call
    const normalizedPhone = normalizePhoneNumber(displayPhone);

    // Make the call
    callHandler({
      From: config.twilio.phoneNumber,
      To: normalizedPhone,
      appointmentId: appointment?.appointmentId,
    });
  };

  const getIconButton = () => {
    if (!twilioCallHandlers) return null;

    const { hangup, mute } = twilioCallHandlers;

    let displayPhone = mode === 'waiting' ? appointment?.patientPhoneNumber || phoneNumber : phoneNumber;
    if (displayPhone && !displayPhone.startsWith('+')) {
      displayPhone = `+${displayPhone}`;
    }

    // Handle connected state with mute button
    if (twilioCallState === TWILIO_CALL_STATE.CONNECTED) {
      return (
        <Box display="flex" alignItems="center" gap="8px">
          <IconButton
            variant={iconBtnType.PRIMARY}
            className={classes.iconBtn}
            onClick={() => {
              mute(isAudio);
              toggleMic();
            }}
            icon={isAudio ? 'mic-on' : 'mic-off'}
          />
          <Icons className={classes.hangupBtn} glyph="call-hangup" color={colors.white} onClick={hangup} />
        </Box>
      );
    }

    // Handle ringing/connecting state
    if (twilioCallState === TWILIO_CALL_STATE.CONNECTING || twilioCallState === TWILIO_CALL_STATE.RINGING) {
      return (
        <Icons className={classes.hangupBtn} glyph="call-hangup" color={colors.white} onClick={hangup} />
      );
    }

    // Handle idle/disconnected state (default)
    return (
      <Button
        variant={btnType.OUTLINE}
        className={clsx([classes.actionBtn, classes.callBtn])}
        onClick={handlePhoneCall}
        disabled={!displayPhone}
      >
        <Icons glyph="phone-outlined" />
      </Button>
    );
  };

  const getCardContent = () => {
    const displayPhone = mode === 'waiting' ? appointment?.patientPhoneNumber || phoneNumber : phoneNumber;
    const formattedPhone = displayPhone ? formatPhoneNumber(displayPhone) : 'No phone number added';

    if (twilioCallState === TWILIO_CALL_STATE.CONNECTED) {
      const minutes = Math.floor(callConnectedTimer / 60);
      const seconds = callConnectedTimer % 60;
      return (
        <Box display="flex" alignItems="center" gap="6px">
          <img src={connectedImage} alt="connected" />
          <Box className={classes.actionDetailsBox}>
            <p className={classes.cardTitle}>
              Connected {minutes}:{seconds.toString().padStart(2, '0')}
            </p>
            <p className={classes.cardDescription}>{formattedPhone}</p>
          </Box>
        </Box>
      );
    }

    if (twilioCallState === TWILIO_CALL_STATE.RINGING || twilioCallState === TWILIO_CALL_STATE.CONNECTING) {
      return (
        <Box display="flex" alignItems="center" gap="6px">
          <img src={ringingImage} alt="ringing" />
          <Box className={classes.actionDetailsBox}>
            <p className={classes.cardTitle}>Ringing...</p>
            <p className={classes.cardDescription}>{formattedPhone}</p>
          </Box>
        </Box>
      );
    }

    // Default: IDLE or DISCONNECTED
    return (
      <Box className={classes.actionDetailsBox}>
        <p className={classes.cardTitle}>Call the member directly</p>
        <p className={classes.cardDescription}>{formattedPhone}</p>
      </Box>
    );
  };

  const renderActionCard = (
    title,
    description,
    buttonText,
    buttonVariant,
    buttonClass,
    onClick,
    descriptionClass,
  ) => (
    <Box className={classes.actionCard}>
      <Box className={classes.actionDetailsBox}>
        <p className={classes.cardTitle}>{title}</p>
        <p className={clsx(classes.cardDescription, descriptionClass)}>{description}</p>
      </Box>
      <Button variant={buttonVariant} className={buttonClass} onClick={onClick}>
        {buttonText}
      </Button>
    </Box>
  );

  // Determine title and subtitle based on mode
  let titleContent;
  let subtitleContent;
  let showIcon = false;

  if (mode === 'waiting') {
    const memberFirstName = appointment?.patientFirstName || firstName || 'Member';

    if (timeRemaining === 'expired') {
      titleContent = 'You can mark the appointment as no show.';
      subtitleContent = null;
    } else if (isGracePeriod) {
      titleContent = (
        <>
          {memberFirstName} is running late: <span className={classes.timerText}>{timeRemaining}</span>
        </>
      );
      subtitleContent =
        "If you're unable to reach the member, mark them as a no-show after waiting for them for another 5 minutes.";
    } else {
      titleContent = (
        <>
          Your appointment starts in <span className={classes.timerText}>{timeRemaining}</span>
        </>
      );
      subtitleContent =
        'Sit tight — your patient will join you here as soon as they enter the waiting room. If you need to reach out, you can use the contact options below to get in touch with the member.';
    }
  } else if (mode === 'phone-connected') {
    const memberFirstName = appointment?.patientFirstName || firstName || 'Member';
    titleContent = `Call connected with ${memberFirstName}`;
    subtitleContent =
      "You're currently connected via phone. Continue with your appointment and fill out the documentation on the right.";
    showIcon = false;
  } else {
    showIcon = true;
    titleContent = `${firstName} got disconnected from the session.`;
    subtitleContent =
      "Sit tight, we'll connect you to them when they're back online. You can keep filling out your documentation.";
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.contentBox}>
        {showIcon && (
          <img src={InternetDisconnected} className={classes.disconnectedIcon} alt={'Member Disconnected'} />
        )}
        <Box className={classes.titleContainer}>
          <p className={classes.title}>{titleContent}</p>
        </Box>
        {subtitleContent && (
          <Box className={classes.subTitleContainer}>
            <p className={classes.subTitle}>{subtitleContent}</p>
          </Box>
        )}
        {mode === 'waiting' && !mediaPermissionsGranted ? (
          <Box className={classes.permissionsContainer}>
            <p className={classes.cardTitle}>You have to grant video and audio access</p>
            <p className={classes.cardDescription}>
              Here's{' '}
              <a
                href="https://support.google.com/chrome/answer/2693767"
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                quick guide
              </a>{' '}
              on how to accept access on google chrome
            </p>
          </Box>
        ) : (
          !memberMediaPermissionsGranted &&
          mode !== 'phone-connected' && (
            <Box className={classes.permissionsContainer}>
              <p className={classes.cardTitle}>
                {appointment?.patientFirstName || firstName} can't join the session
              </p>
              <p className={classes.cardDescription}>
                It's likely an issue with their audio and video permissions
              </p>
            </Box>
          )
        )}
        {/* Call the member card - works in all modes */}
        <Box className={classes.actionCard}>
          {getCardContent()}
          {getIconButton()}
        </Box>
        {mode === 'waiting' && (
          <Box className={classes.actionCard}>
            <Box className={classes.textSection}>
              <Box display="flex" flexDirection="column" flex={1} gap={1}>
                <p className={classes.cardTitle}>Send them a text</p>
                <Input
                  fullWidth
                  value={textMessage}
                  size={inputSize.M}
                  placeholder="Write your text here"
                  onChange={e => setTextMessage(e.target.value)}
                  disabled={isSendingText}
                  onKeyPress={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendText();
                    }
                  }}
                />
              </Box>
              <Button
                variant={btnType.PRIMARY}
                className={classes.primaryBtn}
                onClick={handleSendText}
                disabled={isSendingText || !textMessage.trim()}
              >
                {isSendingText ? 'Sending...' : 'Send text'}
              </Button>
            </Box>
          </Box>
        )}
        {renderActionCard(
          'Chat with the care team',
          `${Math.round(remainingDuration)} min${remainingDuration > 1 ? 's' : ''} left in session`,
          'Go to chat',
          btnType.OUTLINE,
          classes.outlineBtn,
          () => onMemberActionClick(MEMBER_ACTIONS.CHAT),
          mode === 'disconnected' && Math.round(remainingDuration) <= 5 ? classes.danger : '',
        )}
      </Box>
    </Box>
  );
};

export { MemberDisconnected };
